import { store } from '@/app/store';
import { mergeMessageById, prependMessages } from '@/features/chats/model/conversation/reducer';
import { insertDateLabels } from '@/shared/utils';
import { buildGroupedMediaObject, canGroupMessages, groupMediaMessages } from '../utils/mediaGrouping';

const messageBuffer = new Map(); // chatId -> messages[]
const timers = new Map(); // chatId -> timeoutId
const pendingLoadChats = new Set(); // chatIds whose initial conversation API is in flight

const BATCH_INTERVAL = 500;

/**
 * Collect every msgId already present in the store for this chat,
 * including msgIds nested inside groupedMedia containers.
 */
const collectExistingMsgIds = chatMessages => {
   const ids = new Set();
   for (const m of chatMessages) {
      if (m.msgId) ids.add(m.msgId);
      if (m.type === 'groupedMedia' && Array.isArray(m.messages)) {
         for (const inner of m.messages) {
            if (inner.msgId) ids.add(inner.msgId);
         }
      }
   }
   return ids;
};

/**
 * INTERNAL: Flush + Dispatch (used by timer)
 */
const flushAndDispatchInternal = chatId => {
   // The chat's initial conversation API is still in flight. Dispatching now
   // would drop the messages (no targetChat in Redux yet) — and an immediate
   // send (dispatchMessagesToConversation immediate:true) reaches here directly,
   // bypassing the bufferMessages hold. Keep the buffer intact; markChatLoadEnd
   // will flush it once the API resolves.
   if (pendingLoadChats.has(chatId)) return;

   let messages = messageBuffer.get(chatId) || [];
   messageBuffer.delete(chatId);

   if (!messages.length) return;

   const conversationState = store.getState().conversationData.data;
   const existingChat = conversationState[chatId];
   const existingIds = existingChat ? collectExistingMsgIds(existingChat.messages || []) : new Set();

   // ✅ 0. Partition against store: buffered messages whose msgId already exists
   // get dispatched as an in-place merge (buffer wins, local protected media stays).
   // The remainder continue through the normal prepend pipeline.
   if (existingIds.size) {
      const overlapping = [];
      const fresh = [];
      for (const m of messages) {
         if (m.msgId && existingIds.has(m.msgId)) overlapping.push(m);
         else fresh.push(m);
      }
      for (const m of overlapping) {
         store.dispatch(mergeMessageById({ chatId, message: m }));
      }
      messages = fresh;
      if (!messages.length) return;
   }

   // ✅ 1. Ensure newest → oldest
   messages = [...messages].sort((a, b) => b.timestamp - a.timestamp);

   // ✅ 2. Apply grouping on full batch
   let processedMessages = groupMediaMessages(messages);

   const conversationData = store.getState().conversationData.data;
   const targetChat = conversationData[chatId];

   let stitchedMessageId = null;

   // ✅ 3. Stitch with existing messages
   if (targetChat && targetChat.messages?.length > 0) {
      const existingNewest = targetChat.messages[0];
      const incomingOldest = processedMessages[processedMessages.length - 1];

      const existingMessages = existingNewest.type === 'groupedMedia' ? existingNewest.messages : [existingNewest];

      const incomingMessages = incomingOldest.type === 'groupedMedia' ? incomingOldest.messages : [incomingOldest];

      if (canGroupMessages(existingNewest, incomingOldest)) {
         const combined = [...existingMessages, ...incomingMessages];

         // ✅ keep newest → oldest
         combined.sort((a, b) => b.timestamp - a.timestamp);

         processedMessages[processedMessages.length - 1] = buildGroupedMediaObject(combined);

         stitchedMessageId = existingNewest.msgId;
      }
   }

   // ✅ 4. Insert date labels AFTER grouping
   const finalMessages = insertDateLabels(processedMessages);

   // ✅ 5. Dispatch
   if (targetChat && !targetChat?.hasMoreNewer) {
      store.dispatch(
         prependMessages({
            chatId,
            messages: finalMessages,
            stitchedMessageId,
         }),
      );
   }
};

/**
 * Add messages to buffer + start batching timer
 */
export const bufferMessages = (chatId, messages) => {
   if (!messageBuffer.has(chatId)) {
      messageBuffer.set(chatId, []);
   }

   const existing = messageBuffer.get(chatId);

   // Maintain newest-first order
   messageBuffer.set(chatId, [...messages, ...existing]);

   // Conversation API hasn't completed yet — hold the data, do NOT schedule a timer.
   // markChatLoadEnd will flush once the API resolves.
   if (pendingLoadChats.has(chatId)) return;

   // If timer already exists → do nothing
   if (timers.has(chatId)) return;

   const timer = setTimeout(() => {
      flushAndDispatchInternal(chatId);
      timers.delete(chatId);
   }, BATCH_INTERVAL);

   timers.set(chatId, timer);
};

/**
 * Mark the chat's initial conversation API as in flight.
 * While set, bufferMessages accumulates without scheduling a dispatch.
 */
export const markChatLoadStart = chatId => {
   pendingLoadChats.add(chatId);
   // Cancel any pending timer — we'll dispatch only after the API resolves.
   if (timers.has(chatId)) {
      clearTimeout(timers.get(chatId));
      timers.delete(chatId);
   }
};

/**
 * Mark the chat's initial conversation API as completed.
 * Flushes whatever was buffered while the API was in flight (with dedupe).
 */
export const markChatLoadEnd = chatId => {
   pendingLoadChats.delete(chatId);
   flushAndDispatch(chatId);
};

/**
 * Manual flush (used for initial load)
 */
export const flushAndDispatch = chatId => {
   // Clear timer if exists (avoid double dispatch)
   if (timers.has(chatId)) {
      clearTimeout(timers.get(chatId));
      timers.delete(chatId);
   }

   flushAndDispatchInternal(chatId);
};

/**
 * Optional helpers
 */
export const flushBufferedMessages = chatId => {
   const messages = messageBuffer.get(chatId) || [];
   messageBuffer.delete(chatId);
   return messages;
};

export const clearBufferedMessages = chatId => {
   messageBuffer.delete(chatId);

   if (timers.has(chatId)) {
      clearTimeout(timers.get(chatId));
      timers.delete(chatId);
   }
};

// ──────────────────────────────────────────────────────────────────────────────
// Addressable-buffer helpers
//
// While a chat is in pendingLoadChats, the live buffer holds messages that are
// NOT yet in the Redux store. Any reducer that walks state.data[chatId].messages
// (acks, edits, recall, delete, favourite, media status) will miss those msgIds.
// These helpers let callers patch the buffered copy directly so the flushed
// data ends up current.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Locate a message in the buffer for `chatId`, including children of
 * groupedMedia containers.
 */
export const findBufferedMessage = (chatId, msgId) => {
   const arr = messageBuffer.get(chatId);
   if (!arr) return { message: null, parentGroup: null, index: -1, childIndex: -1 };
   for (let i = 0; i < arr.length; i++) {
      const m = arr[i];
      if (m.msgId === msgId) {
         return { message: m, parentGroup: null, index: i, childIndex: -1 };
      }
      if (m.type === 'groupedMedia' && Array.isArray(m.messages)) {
         const ci = m.messages.findIndex(x => x.msgId === msgId);
         if (ci !== -1) {
            return { message: m.messages[ci], parentGroup: m, index: i, childIndex: ci };
         }
      }
   }
   return { message: null, parentGroup: null, index: -1, childIndex: -1 };
};

/**
 * If `msgId` is in the buffer, replace it with `patchFn(current)`.
 * `patchFn` must return the new whole-message object.
 * Returns true if a patch was applied.
 */
export const patchBufferedMessage = (chatId, msgId, patchFn) => {
   const { message, parentGroup, index, childIndex } = findBufferedMessage(chatId, msgId);
   if (!message) return false;
   const next = patchFn(message);
   if (!next) return false;
   if (parentGroup) {
      parentGroup.messages[childIndex] = next;
   } else {
      const arr = messageBuffer.get(chatId);
      arr[index] = next;
   }
   return true;
};

/**
 * Remove `msgIds` from the buffer, stripping from inside groupedMedia containers.
 * Empty groups are removed; single-child groups are ungrouped.
 */
export const removeBufferedMessages = (chatId, msgIds) => {
   const arr = messageBuffer.get(chatId);
   if (!arr || !arr.length) return;
   const ids = new Set(Array.isArray(msgIds) ? msgIds : [msgIds]);
   const next = [];
   for (const m of arr) {
      if (ids.has(m.msgId)) continue;
      if (m.type === 'groupedMedia' && Array.isArray(m.messages)) {
         const kept = m.messages.filter(c => !ids.has(c.msgId));
         if (kept.length === 0) continue;
         if (kept.length === 1) {
            next.push(kept[0]);
            continue;
         }
         next.push(buildGroupedMediaObject(kept));
         continue;
      }
      next.push(m);
   }
   messageBuffer.set(chatId, next);
};
